<template>
  <div
    class="overscroll-x-contain overflow-x-auto bg-surface-default-mid py-16"
  >
    <ul
      class="flex w-max flex-nowrap items-start gap-8 px-24 md:gap-16 lg:px-[max(2rem,calc((100vw-1184px)/2+2rem))]"
    >
      <li
        v-for="criterion in config.criteria"
        :key="criterion.kind"
        class="w-[288px] h-[320px] shrink-0"
      >
        <FinderScreensizeTile
          v-if="criterion.kind === 'screensize' && screensizeCriterion"
          v-model="selectedScreensizeValues"
          :criterion="screensizeCriterion"
          @select-range="handleSelectScreensizeRange"
        />
        <FinderUsagesTile
          v-else-if="criterion.kind === 'usages' && usagesCriterion"
          v-model="selectedUsagesValues"
          :criterion="usagesCriterion"
          :facet="usageFacet"
        />
        <FinderPriceTile
          v-else-if="criterion.kind === 'budget' && budgetCriterion"
          v-model="price"
          :criterion="budgetCriterion"
          :price-facet
        />
        <FinderConditionsTile
          v-else-if="criterion.kind === 'conditions' && conditionsCriterion"
          v-model="selectedConditionValues"
          :criterion="conditionsCriterion"
          :facet="conditionsFacet"
        />
        <FinderRequirementsTile
          v-else-if="criterion.kind === 'requirements' && requirementsCriterion"
          v-model="filters"
          :criterion="requirementsCriterion"
          :facets="remainingFacets"
        />
      </li>
    </ul>

    <PlpContainer v-if="activeFiltersCount > 0" class="mt-16">
      <FinderJumpToResults class="mb-16" to-anchor-id="results" />
    </PlpContainer>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import type {
  Facet,
  UiPriceFacet,
} from '@backmarket/nuxt-layer-recommendation/filtering/types'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import FinderJumpToResults from '../../components/Finder/components/FinderJumpToResults/FinderJumpToResults.vue'
import PlpContainer from '../../components/PlpContainer/PlpContainer.vue'
import { findCriterion, useFinder } from '../../composables/useFinder/useFinder'
import { getMacbookFinderConfig } from '../../composables/useFinder/useFinder.config.macbook'
import { isFacetBackedCriterion } from '../../composables/useFinder/useFinder.types'

import FinderConditionsTile from './components/FinderConditionsTile/FinderConditionsTile.vue'
import FinderPriceTile from './components/FinderPriceTile/FinderPriceTile.vue'
import FinderRequirementsTile from './components/FinderRequirementsTile/FinderRequirementsTile.vue'
import FinderScreensizeTile from './components/FinderScreensizeTile/FinderScreensizeTile.vue'
import FinderUsagesTile from './components/FinderUsagesTile/FinderUsagesTile.vue'

const props = defineProps<{
  facets: Facet[]
  priceFacet?: UiPriceFacet
}>()

const filters = defineModel<Record<string, string[]>>('filters', {
  default: () => ({}),
})
const price = defineModel<[number, number] | undefined>('price')

const i18n = useI18n()

const config = getMacbookFinderConfig(i18n)
const screensizeCriterion = findCriterion(config, 'screensize')
const usagesCriterion = findCriterion(config, 'usages')
const budgetCriterion = findCriterion(config, 'budget')
const conditionsCriterion = findCriterion(config, 'conditions')
const requirementsCriterion = findCriterion(config, 'requirements')

const configuredFacetNames = new Set(
  config.criteria
    .filter(isFacetBackedCriterion)
    .map((criterion) => criterion.facetName),
)

const remainingFacets = computed(() =>
  props.facets.filter((facet) => !configuredFacetNames.has(facet.name)),
)

const finder = useFinder(config, filters.value, price)

const usageFacet = computed(() =>
  props.facets.find((facet) => facet.name === usagesCriterion?.facetName),
)

const conditionsFacet = computed(() =>
  props.facets.find((facet) => facet.name === conditionsCriterion?.facetName),
)

const selectedScreensizeValues = computed<string[]>({
  get() {
    if (!screensizeCriterion) return []

    return filters.value[screensizeCriterion.facetName] ?? []
  },
  set(values) {
    if (!screensizeCriterion) return

    finder.setCriterionValues(screensizeCriterion, values)
  },
})

function handleSelectScreensizeRange([min, max]: [number, number]) {
  if (!screensizeCriterion) return

  if (
    min === screensizeCriterion.rangeMin &&
    max === screensizeCriterion.rangeMax
  ) {
    finder.setCriterionValues(screensizeCriterion, [])

    return
  }

  const inRangeValues = screensizeCriterion.values.filter((value) => {
    const size = Number.parseInt(value, 10)

    return Number.isFinite(size) && size >= min && size <= max
  })

  finder.setCriterionValues(screensizeCriterion, inRangeValues)
}

const selectedUsagesValues = computed<string[]>({
  get() {
    if (!usagesCriterion) return []

    return filters.value[usagesCriterion.facetName] ?? []
  },
  set(values) {
    if (!usagesCriterion) return

    finder.setCriterionValues(usagesCriterion, values)
  },
})

const selectedConditionValues = computed<string[]>({
  get() {
    if (!conditionsCriterion) return []

    return filters.value[conditionsCriterion.facetName] ?? []
  },
  set(optionIds) {
    if (!conditionsCriterion) return

    finder.setCriterionValues(conditionsCriterion, optionIds)
  },
})

const activeFiltersCount = computed(
  () => Object.values(filters.value).flat().length + (price.value ? 1 : 0),
)
</script>
